import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { sendPasswordResetEmail, type ActionCodeSettings } from 'firebase/auth';
import { auth } from '../auth/firebase';
import {
  AuthLayout,
  AuthError,
  authInputClass,
  authButtonClass,
} from '../components/auth/AuthLayout';
import { extractFirebaseAuthError } from '../auth/firebaseErrors';
import { captureFunnelEvent } from '../services/telemetry';

/** After completing the reset on Firebase's hosted handler, return the user to
 * the sign-in page. The origin must be an authorized domain. */
function resetActionSettings(): ActionCodeSettings {
  return {
    url: `${window.location.origin}/sign-in`,
    handleCodeInApp: false,
  };
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    document.title = 'Reset password - PineappleMusicLab';
  }, []);

  const trackResetRequested = (delivered: boolean, errorCode?: string) => {
    captureFunnelEvent('password_reset_requested', {
      email,
      delivered,
      error_code: errorCode ?? null,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      await sendPasswordResetEmail(auth, email, resetActionSettings());
      trackResetRequested(true);
      setSent(true);
    } catch (err) {
      const code = (err as { code?: string })?.code;
      // Don't reveal whether an account exists: treat "user not found" as a
      // success so the response is identical for valid and invalid emails.
      if (code === 'auth/user-not-found') {
        // Still a real request from this device, just for an address we don't
        // have — worth recording, since the user believes they requested a reset.
        trackResetRequested(false, code);
        setSent(true);
      } else {
        trackResetRequested(false, code);
        setError(extractFirebaseAuthError(err));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <AuthLayout
        title="Check your email"
        subtitle={`If an account exists for ${email}, we've sent a link to set a new password. Can't find it? Check your spam or junk folder.`}
      >
        <Link to="/sign-in" className={authButtonClass + 'block text-center'}>
          Back to sign in
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Reset your password"
      subtitle="Enter your email and we'll send you a link to set a new password."
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        <input
          className={authInputClass}
          type="email"
          autoComplete="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <button className={authButtonClass} type="submit" disabled={submitting}>
          {submitting ? 'Sending…' : 'Send reset link'}
        </button>
      </form>

      <AuthError message={error} />

      <p className="mt-6 text-sm text-gray-500">
        Remembered it?{' '}
        <Link to="/sign-in" className="text-yellow-600 underline">
          Sign in
        </Link>
      </p>
    </AuthLayout>
  );
}
